import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { configurePdfUnicodeFont, PDF_UNICODE_FONT } from './pdfFont';
import { getLocale, getStoredLanguage, localizeJsPdfInstance, localizePdfText } from './localization';

interface InvoicePdfOptions {
  invoice: any;
  organization?: any;
  contact?: any;
  brandingTheme?: any;
  calculateSubtotal?: (item: any) => number;
  calculateTax?: (item: any) => number;
}

const PAGE_MARGIN = 14;
const FOOTER_SPACE = 18;
const DARK: [number, number, number] = [20, 20, 20];
const MUTED: [number, number, number] = [100, 100, 100];
const LIGHT: [number, number, number] = [232, 232, 230];

const numberValue = (value: unknown) => Number(value) || 0;
const isObject = (value: unknown): value is Record<string, any> => !!value && typeof value === 'object' && !Array.isArray(value);

const textValue = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (Array.isArray(value)) return value.map(textValue).filter(Boolean).join(', ');
  if (!isObject(value)) return '';

  const direct = value.value || value.formattedAddress || value.formatted_address || value.fullAddress || value.address;
  if (direct && direct !== value) return textValue(direct);
  if (value.number) return [value.countryCode, value.number].map(textValue).filter(Boolean).join(' ');

  return [
    value.line1 || value.addressLine1 || value.street,
    value.line2 || value.addressLine2,
    value.city,
    value.state || value.region,
    value.postalCode || value.postcode || value.zip,
    value.country,
  ].map(textValue).filter(Boolean).join(', ');
};

const firstText = (...values: unknown[]) => values.map(textValue).find(Boolean) || '';
const splitLines = (value: unknown) => textValue(value).split(/\r?\n/).map(line => line.trim()).filter(Boolean);
const parseTaxRate = (value: unknown) => {
  const match = String(value || '').match(/[0-9]+(?:\.[0-9]+)?/);
  return match ? numberValue(match[0]) / 100 : 0;
};

const defaultSubtotal = (item: any) => {
  const beforeDiscount = numberValue(item.quantity) * numberValue(item.unitPrice);
  const discount = item.discountType === 'amount'
    ? numberValue(item.discount)
    : beforeDiscount * numberValue(item.discount) / 100;
  return Math.max(0, beforeDiscount - discount);
};

const defaultTax = (item: any) => defaultSubtotal(item) * parseTaxRate(item.taxRate);
const money = (value: unknown) => numberValue(value).toLocaleString(getLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (value: unknown) => value
  ? new Date(String(value)).toLocaleDateString(getLocale(), { year: 'numeric', month: 'short', day: 'numeric' })
  : '—';

export async function generateInvoicePdf({
  invoice,
  organization,
  contact,
  brandingTheme,
  calculateSubtotal,
  calculateTax,
}: InvoicePdfOptions) {
  const subtotalFor = calculateSubtotal || defaultSubtotal;
  const taxFor = calculateTax || defaultTax;
  const pdf = localizeJsPdfInstance(new jsPDF({ unit: 'mm', format: 'a4' }));
  await configurePdfUnicodeFont(pdf);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const right = pageWidth - PAGE_MARGIN;
  const settingsData = isObject(organization?.data) ? organization.data : {};
  const org = { ...settingsData, ...(organization || {}) };
  const customer = contact || {};
  const currency = invoice.currency || org.defaultCurrency || org.default_currency || 'USD';

  const orgName = firstText(org.name, org.organizationName, org.companyName) || 'Company';
  const orgAddress = firstText(
    brandingTheme?.organizationDetails,
    org.address,
    org.billingAddress,
    org.location,
  );
  const orgPhone = firstText(org.phone, org.phoneNumber, org.telephone);
  const orgEmail = firstText(org.email, org.contactEmail);
  const orgWebsite = firstText(org.website, org.websiteUrl);
  const orgTaxNumber = firstText(org.vatNumber, org.taxNumber, org.taxRegistrationNumber, org.trn);

  const customerName = firstText(customer.name, invoice.contactName) || 'Valued Customer';
  const customerAddress = firstText(invoice.billingAddress, customer.billingAddress, customer.address);
  const customerEmail = firstText(customer.email, customer.emails, invoice.contactEmail);
  const customerPhone = firstText(customer.phone, customer.phones);
  const customerTaxNumber = firstText(customer.taxNumber, customer.vatNumber);

  const footerPages = new Set<number>();
  const drawFooter = () => {
    const page = pdf.getNumberOfPages();
    if (footerPages.has(page)) return;
    footerPages.add(page);
    pdf.setDrawColor(...LIGHT);
    pdf.line(PAGE_MARGIN, pageHeight - 12, right, pageHeight - 12);
    pdf.setFont(PDF_UNICODE_FONT, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(`${orgName} • Invoice ${invoice.number || ''}`, PAGE_MARGIN, pageHeight - 7);
    pdf.text(`Page ${page}`, right, pageHeight - 7, { align: 'right' });
  };

  pdf.setTextColor(...DARK);
  pdf.setFont(PDF_UNICODE_FONT, 'bold');
  pdf.setFontSize(18);
  pdf.text(invoice.type === 'credit_note' ? 'CREDIT NOTE' : 'TAX INVOICE', PAGE_MARGIN, 20);
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text(`Invoice ${invoice.number || invoice.id || ''}`, PAGE_MARGIN, 27);

  pdf.setTextColor(...DARK);
  pdf.setFontSize(12);
  pdf.text(orgName, right, 17, { align: 'right' });
  pdf.setFont(PDF_UNICODE_FONT, 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(...MUTED);
  const orgDetails = [
    ...splitLines(orgAddress),
    orgPhone && `Phone: ${orgPhone}`,
    orgEmail && `Email: ${orgEmail}`,
    orgWebsite && `Web: ${orgWebsite}`,
    orgTaxNumber && `Tax ID: ${orgTaxNumber}`,
  ].filter(Boolean);
  if (orgDetails.length) pdf.text(orgDetails.slice(0, 5), right, 23, { align: 'right' });

  pdf.setDrawColor(...DARK);
  pdf.setLineWidth(0.5);
  pdf.line(PAGE_MARGIN, 45, right, 45);

  const infoTop = 54;
  pdf.setFont(PDF_UNICODE_FONT, 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  pdf.text('BILL TO', PAGE_MARGIN, infoTop);
  pdf.setFontSize(11.5);
  pdf.setTextColor(...DARK);
  pdf.text(customerName, PAGE_MARGIN, infoTop + 8);
  pdf.setFont(PDF_UNICODE_FONT, 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(...MUTED);
  const customerDetails = [
    ...splitLines(customerAddress),
    customerPhone && `Phone: ${customerPhone}`,
    customerEmail && `Email: ${customerEmail}`,
    customerTaxNumber && `Tax ID: ${customerTaxNumber}`,
  ].filter(Boolean);
  if (customerDetails.length) pdf.text(customerDetails.slice(0, 5), PAGE_MARGIN, infoTop + 14);

  const metaLabelX = right - 66;
  pdf.setFont(PDF_UNICODE_FONT, 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  pdf.text('ISSUE DATE', metaLabelX, infoTop);
  pdf.text('DUE DATE', metaLabelX, infoTop + 7);
  if (invoice.reference) pdf.text('REFERENCE', metaLabelX, infoTop + 14);
  pdf.setFont(PDF_UNICODE_FONT, 'normal');
  pdf.setTextColor(...DARK);
  pdf.text(formatDate(invoice.date), right, infoTop, { align: 'right' });
  pdf.text(formatDate(invoice.dueDate), right, infoTop + 7, { align: 'right' });
  if (invoice.reference) pdf.text(String(invoice.reference), right, infoTop + 14, { align: 'right' });

  const items = Array.isArray(invoice.items) ? invoice.items : [];
  const tableData = items.map((item: any) => {
    const beforeDiscount = numberValue(item.quantity) * numberValue(item.unitPrice);
    const discount = item.discountType === 'amount'
      ? numberValue(item.discount)
      : beforeDiscount * numberValue(item.discount) / 100;
    return [
      firstText(item.description, item.name, item.item) || 'Unnamed item',
      String(numberValue(item.quantity)),
      `${currency} ${money(item.unitPrice)}`,
      discount > 0 ? `${currency} ${money(discount)}` : '—',
      `${currency} ${money(numberValue(subtotalFor(item)) + numberValue(taxFor(item)))}`,
    ];
  });

  autoTable(pdf, {
    startY: 88,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: FOOTER_SPACE },
    head: [[
      localizePdfText('Description'),
      localizePdfText('Qty'),
      localizePdfText('Unit Price'),
      localizePdfText('Discount'),
      localizePdfText('Amount'),
    ]],
    body: tableData.length ? tableData : [[localizePdfText('No items'), '—', '—', '—', `${currency} 0.00`]],
    theme: 'grid',
    styles: {
      font: PDF_UNICODE_FONT,
      fontSize: 8.5,
      textColor: DARK,
      cellPadding: 3.5,
      lineColor: LIGHT,
      lineWidth: 0.2,
      valign: 'middle',
      halign: getStoredLanguage() === 'ar' ? 'right' : 'left',
    },
    headStyles: {
      fillColor: DARK,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: getStoredLanguage() === 'ar' ? 'right' : 'left',
    },
    alternateRowStyles: { fillColor: [250, 250, 249] },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 18, halign: 'right' },
      2: { cellWidth: 32, halign: 'right' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 35, halign: 'right' },
    },
    didDrawPage: drawFooter,
  });

  let cursor = numberValue((pdf as any).lastAutoTable?.finalY) + 10;
  const subtotal = items.reduce((sum: number, item: any) => sum + numberValue(subtotalFor(item)), 0);
  const tax = items.reduce((sum: number, item: any) => sum + numberValue(taxFor(item)), 0);
  const total = Number.isFinite(Number(invoice.totalAmount)) ? numberValue(invoice.totalAmount) : subtotal + tax;
  const paid = numberValue(invoice.paymentReceived ?? invoice.paidAmount);
  const totalsHeight = paid > 0 ? 48 : 34;
  if (cursor + totalsHeight > pageHeight - FOOTER_SPACE) {
    pdf.addPage();
    cursor = 20;
  }

  const totalX = right - 82;
  pdf.setFont(PDF_UNICODE_FONT, 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text('Subtotal', totalX, cursor);
  pdf.text(`${currency} ${money(subtotal)}`, right, cursor, { align: 'right' });
  pdf.text('Tax', totalX, cursor + 7);
  pdf.text(`${currency} ${money(tax)}`, right, cursor + 7, { align: 'right' });
  pdf.setFillColor(...DARK);
  pdf.roundedRect(totalX - 4, cursor + 12, 86, 13, 2, 2, 'F');
  pdf.setFont(PDF_UNICODE_FONT, 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text('TOTAL', totalX, cursor + 20);
  pdf.text(`${currency} ${money(total)}`, right - 4, cursor + 20, { align: 'right' });
  cursor += 32;

  if (paid > 0) {
    pdf.setFont(PDF_UNICODE_FONT, 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...MUTED);
    pdf.text('Amount paid', totalX, cursor);
    pdf.text(`${currency} ${money(paid)}`, right, cursor, { align: 'right' });
    pdf.setFont(PDF_UNICODE_FONT, 'bold');
    pdf.setTextColor(...DARK);
    pdf.text('Amount due', totalX, cursor + 7);
    pdf.text(`${currency} ${money(Math.max(0, total - paid))}`, right, cursor + 7, { align: 'right' });
    cursor += 16;
  }

  const sections = [
    { title: 'PAYMENT INSTRUCTIONS', value: invoice.paymentInstructions },
    { title: 'TERMS & CONDITIONS', value: invoice.termsAndConditions },
    { title: 'NOTES', value: invoice.notes || brandingTheme?.invoiceFooterText },
  ].filter(section => textValue(section.value));

  for (const section of sections) {
    const contentLines = pdf.splitTextToSize(textValue(section.value), 110);
    const sectionHeight = 14 + contentLines.length * 4;
    if (cursor + sectionHeight > pageHeight - FOOTER_SPACE) {
      drawFooter();
      pdf.addPage();
      cursor = 20;
    }
    pdf.setDrawColor(...LIGHT);
    pdf.line(PAGE_MARGIN, cursor, right, cursor);
    pdf.setFont(PDF_UNICODE_FONT, 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(section.title, PAGE_MARGIN, cursor + 8);
    pdf.setFont(PDF_UNICODE_FONT, 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...DARK);
    pdf.text(contentLines, PAGE_MARGIN, cursor + 15);
    cursor += sectionHeight;
  }

  drawFooter();
  pdf.save(`${invoice.number || 'invoice'}_Invoice.pdf`);
}
